import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from '@nula/core-ui';
import { isDesktop } from '@/lib/platform';
import type { CompanionManifest } from '@/lib/companion/types';
import { getCompanion } from '@/lib/companion/registry';
import { normalizeManifest } from '@/lib/companion/manifest-loader';
import {
  readCompanionPrefs, setCompanionPosition, useCompanionPrefs,
} from '@/lib/companion/use-companion-prefs';
import { createFrameStore } from '../types/frame';
import { useCompanionEngine } from '../hooks/use-companion-engine';
import { useCompanionDrag } from '../hooks/use-companion-drag';
import { useActivitySources } from '../hooks/use-activity-sources';
import { readWorld, isOverDragRegion } from '../utils/measure-dom';
import { clampToBounds, fromNormalized, toNormalized } from '../utils/clamp-position';
import { CompanionSprite } from './companion-sprite';
import styles from './companion-layer.module.css';

const REACTION_MS = 1100;
const AFFECTION_CHANCE = 0.25;

const EMPTY_MANIFEST: CompanionManifest = {
  id: '',
  displayName: '',
  spritesheet: '',
  frame: { width: 192, height: 208, columns: 8, rows: 11 },
  loopMs: 1100,
  animations: { idle: { row: 0, frames: 1 } },
};

export interface CompanionLayerProps {
  rng?: () => number;
}

export function CompanionLayer({ rng = Math.random }: CompanionLayerProps = {}) {
  useActivitySources();
  const { enabled, companionId, scale } = useCompanionPrefs();
  const [manifest, setManifest] = useState<CompanionManifest | null>(null);
  const positionRef = useRef<HTMLElement | null>(null);
  const reactionTimerRef = useRef(0);

  // One frame store for the life of the mount: switching companions must not
  // reset position or start a second behavior loop.
  const frames = useMemo(() => createFrameStore(), []);

  const entry = getCompanion(companionId);

  useEffect(() => {
    if (!entry) { setManifest(null); return; }

    let cancelled = false;
    const img = new Image();

    const build = () => {
      if (cancelled) return;
      setManifest(normalizeManifest(entry.raw, {
        spritesheet: entry.spritesheet,
        atlasWidth: img.naturalWidth,
        atlasHeight: img.naturalHeight,
        fallbackId: entry.id,
      }));
    };

    img.addEventListener('load', build);
    img.src = entry.spritesheet;
    if (img.complete && img.naturalWidth > 0) build();

    return () => { cancelled = true; img.removeEventListener('load', build); };
  }, [entry]);

  const displayWidth = manifest ? Math.round(manifest.frame.width * scale) : 0;
  const displayHeight = manifest ? Math.round(manifest.frame.height * scale) : 0;

  const boundsNow = useCallback(() => (
    readWorld(displayWidth)?.content
      ?? { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
  ), [displayWidth]);

  const controller = useCompanionEngine({
    manifest: manifest ?? EMPTY_MANIFEST,
    frames,
    enabled: enabled && manifest !== null,
    displayWidth,
    displayHeight,
  });

  useEffect(() => {
    if (!manifest) return;
    const b = boundsNow();
    frames.set(fromNormalized(readCompanionPrefs().position, b, displayWidth, displayHeight));
  }, [manifest, frames, displayWidth, displayHeight, boundsNow]);

  // Disabling the companion never unmounts CompanionLayer — it only guards
  // the physics effect and this component's render output — so an
  // in-progress drag's pointer handlers never fire and interaction ownership
  // would otherwise survive the toggle unreleased.
  useEffect(() => {
    if (!enabled) {
      window.clearTimeout(reactionTimerRef.current);
      reactionTimerRef.current = 0;
      controller.release('interaction');
    }
  }, [enabled, controller]);

  useEffect(() => () => window.clearTimeout(reactionTimerRef.current), []);

  useEffect(() => {
    if (!enabled || !manifest) return;

    const node = positionRef.current;
    if (!node) return;

    const apply = () => {
      const { x, y } = frames.get();
      node.style.setProperty('--companion-x', `${Math.round(x)}px`);
      node.style.setProperty('--companion-y', `${Math.round(y)}px`);
    };

    apply();
    return frames.subscribe(apply);
  }, [enabled, manifest, frames]);

  const [stateAttr, setStateAttr] = useState(() => frames.get().state);
  useEffect(() => frames.subscribe(() => {
    const next = frames.get().state;
    setStateAttr((prev) => (prev === next ? prev : next));
  }), [frames]);

  const drag = useCompanionDrag({
    onDragStart: () => {
      // A reaction from a click moments ago is still pending. Let it run and
      // it resolves to idle on top of the drag that replaced it.
      window.clearTimeout(reactionTimerRef.current);
      reactionTimerRef.current = 0;
      controller.claim('interaction');
      controller.setState('held');
    },
    onDragMove: (pos) => {
      const b = boundsNow();
      frames.set(clampToBounds(pos, b, displayWidth, displayHeight));
    },
    onDragEnd: (pos) => {
      const b = boundsNow();
      const clamped = clampToBounds(pos, b, displayWidth, displayHeight);
      frames.set(clamped);
      setCompanionPosition(toNormalized(clamped, b, displayWidth, displayHeight));
      // Landing is contact, not release. Played unconditionally it fired on the
      // spot wherever the pointer let go, then autonomy resumed a beat later
      // and she fell anyway — one drop read as two.
      if (controller.isGrounded(clamped.x, clamped.y)) {
        controller.transitionTo('landing', 'idle');
      }
      controller.settleAfterDrop();
    },
    onClick: () => {
      controller.react();
      window.clearTimeout(reactionTimerRef.current);
      reactionTimerRef.current = window.setTimeout(() => {
        reactionTimerRef.current = 0;
        controller.setState(rng() < AFFECTION_CHANCE ? 'affectionate' : 'idle');
      }, REACTION_MS);
    },
    onCancel: () => {
      controller.setState('idle');
      controller.release('interaction');
    },
  });

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    const node = positionRef.current;
    // Only a Tauri window can be dragged by its chrome. On web getCurrentWindow
    // throws, and returning early here left the companion undraggable wherever
    // she overlapped a drag region — the tab bar, most of the time.
    if (isDesktop && node && isOverDragRegion(event.clientX, event.clientY, node)) {
      void import('@tauri-apps/api/window').then(({ getCurrentWindow }) => getCurrentWindow().startDragging());
      return;
    }
    drag.onPointerDown(event);
  }, [drag]);

  useEffect(() => {
    if (!enabled || !manifest) return;

    const onResize = () => {
      const b = boundsNow();
      // Re-derive from the normalized value: clamping stale pixels would let a
      // companion parked at an edge drift inward on every resize.
      frames.set(fromNormalized(readCompanionPrefs().position, b, displayWidth, displayHeight));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, manifest, frames, displayWidth, displayHeight, boundsNow]);

  if (!enabled || !manifest) return null;

  return (
    <Flex className={styles.layer} data-companion-self="">
      <Flex
        ref={positionRef}
        className={styles.companion}
        draggable={false}
        data-companion-state={stateAttr}
        {...drag}
        onPointerDown={handlePointerDown}
      >
        <CompanionSprite manifest={manifest} frames={frames} scale={scale} />
      </Flex>
    </Flex>
  );
}
